<script setup lang="ts">
// ================================
// IMPORTS
// ================================

// Vue
import { computed, inject, ref } from "vue"

// Types
import { ConsentRequest } from "@types"
import type { Services } from "@services"
import type { ConsentRequestInvitationDto } from "@core/types/source/consent_request"
import { useFetchWrapper } from "@core/composables"
import { CONSENT_REQUEST_TYPE, CONSENT_REQUEST_STATUS } from "@core/constants"
import type { ConsentSentModalPayload } from "@core/components/source/dialog/consent-request-send-dialog.types"
import { resolveConsentRequestSendInvitation } from "@core/components/source/dialog/consent-request-send.helper"
import { rafraichirPlafondServi } from "@core/composables/plafond-rafraichissement"
import { lirePhrasePlaceDemande } from "@core/composables/consentrequest-place"

// ================================
// TYPES & INTERFACES
// ================================

type Props = {
    consentRequest: ConsentRequest
}

// ================================
// PROPS & EMITS
// ================================

const props = defineProps<Props>()

const emit = defineEmits<{
    "delete:consent": [consentRequestId: string]
    "update:consent-request": [consentRequest: ConsentRequest]
}>()

// ================================
// SERVICES
// ================================

/**
 * Services injectés
 */
const services = inject<Services>("services")!
const {
    $deleteConsentRequest,
    $getConsentRequestChangeOwner,
    $getConsentRequestExtend,
    $getConsentRequestMulti,
    $getConsentRequestShare,
    $getConsentRequestSign,
} = services

// ================================
// DONNÉES CALCULÉES
// ================================

/**
 * Récupère les informations de la demande de consentement depuis les props
 * @returns Données de la demande de consentement
 */
const getConsentRequest = computed(() => {
    return props.consentRequest
})

/**
 * Computed pour extraire toutes les données nécessaires pour les composants enfants
 */
const showConsentSentModal = ref(false)
const consentSentModalPayload = ref<ConsentSentModalPayload | null>(null)

const getConsentRequestData = computed(() => {
    const consentRequest = props.consentRequest
    if (!consentRequest) {
        return {
            state: "",
            type: "",
            createdAt: "",
            requestId: "",
        }
    }

    const isMultiRequest = !!consentRequest._id_consentrequestmulti
    const trackingMails = isMultiRequest
        ? consentRequest._id_consentrequestmulti?.tracking?.mails || []
        : consentRequest.tracking?.mails || []

    // Récupère signedAt depuis tracking.states si disponible
    const signedAt =
        consentRequest.tracking?.states?.find(
            (s) => s.previous_state === CONSENT_REQUEST_STATUS.CREATED
        )?.date ||
        (isMultiRequest
            ? consentRequest._id_consentrequestmulti?.tracking?.states?.find(
                  (s) => s.previous_state === CONSENT_REQUEST_STATUS.CREATED
              )?.date
            : undefined)

    // Récupère le DDA pour PCE (c'est un objet unique, pas un array)
    const dda = consentRequest.source?.operation?.pce?.droit_acces
    const ddaList = dda
        ? [
              {
                  date_creation: new Date().toISOString(), // Pas de date_creation dans le type
                  etat_droit_acces: dda.etat_droit_acces,
              },
          ]
        : undefined

    return {
        state: consentRequest.state || "",
        type: consentRequest.type || "",
        createdAt: consentRequest.createdAt || "",
        signedAt,
        url: consentRequest.url,
        requestId: consentRequest._id || "",
        isMultiRequest,
        trackingMails: trackingMails.map((m) => ({
            type: m.type,
            date: m.date,
        })),
        trackingStates: isMultiRequest
            ? consentRequest._id_consentrequestmulti?.tracking?.states?.map(
                  (s) => ({
                      previous_state: s.previous_state,
                      date: s.date,
                  })
              ) || []
            : consentRequest.tracking?.states?.map((s) => ({
                  previous_state: s.previous_state,
                  date: s.date,
              })) || [],
        userToEmail: consentRequest.user_to?.username,
        sourceIdtype: consentRequest.source?.idtype,
        sourceId: consentRequest.source?._id,
        consentRequestMulti: {
            requestId: consentRequest._id_consentrequestmulti?._id || "",
            url: consentRequest._id_consentrequestmulti?.url || "",
            type: "SIGN",
            state: consentRequest._id_consentrequestmulti?.state || "",
        },
        ddaList,
    }
})

// ================================
// INTERACTIONS UTILISATEUR
// ================================

/**
 * Callback pour envoyer ou relancer une invitation
 * @param config - Configuration pour l'envoi de l'invitation
 */
function onSendInvitation(config: { sendBack: boolean }): void {
    const data = getConsentRequestData.value
    const reqType = getConsentRequest.value?.type
    const { dialogPayload } = resolveConsentRequestSendInvitation(
        {
            isMultiRequest: !!data.isMultiRequest,
            requestId: data.requestId,
            url: data.url,
            type: data.type,
            sourceId: data.sourceId,
            consentRequestMulti: data.consentRequestMulti ?? {
                requestId: "",
                url: "",
                type: "",
            },
        },
        reqType,
        config
    )
    consentSentModalPayload.value = dialogPayload
    showConsentSentModal.value = true
}

function onConsentSentModalDone(): void {
    showConsentSentModal.value = false
    consentSentModalPayload.value = null
}

/**
 * Après envoi d’e-mail depuis la modale : recharge la demande (GET v5 selon le type) et met à jour la ligne du tableau.
 */
async function onConsentInvitationSent(): Promise<void> {
    const p = consentSentModalPayload.value
    const current = getConsentRequest.value
    const id = p?.requestId ?? current?._id
    if (!p || !id || !current?._id) return

    try {
        if ("multiple" in p && p.multiple) {
            if (!$getConsentRequestMulti) return
            const multiFresh = await $getConsentRequestMulti(id)
            const prevMulti = current._id_consentrequestmulti
            const prevMultiObj =
                prevMulti && typeof prevMulti === "object" ? prevMulti : {}
            const mergedMulti = {
                ...prevMultiObj,
                ...multiFresh,
                _id:
                    (multiFresh._id as string | undefined) ??
                    prevMulti?._id ??
                    id,
            }
            emit("update:consent-request", {
                ...current,
                _id_consentrequestmulti: mergedMulti,
            } as ConsentRequest)
            return
        }

        let updated: ConsentRequestInvitationDto
        if (p.kind === "change-owner") {
            if (!$getConsentRequestChangeOwner) return
            updated = await $getConsentRequestChangeOwner(id)
        } else if (p.kind === "share") {
            if (!$getConsentRequestShare) return
            updated = await $getConsentRequestShare(id)
        } else if (p.consentRequestType === CONSENT_REQUEST_TYPE.EXTEND) {
            if (!$getConsentRequestExtend) return
            updated = await $getConsentRequestExtend(id)
        } else {
            if (!$getConsentRequestSign) return
            updated = await $getConsentRequestSign(id)
        }
        const merged = {
            ...current,
            ...updated,
            _id: updated._id ?? current._id,
        } as ConsentRequest
        emit("update:consent-request", merged)
    } catch (error) {
        console.error(
            "Erreur lors du rafraîchissement de la demande de consentement:",
            error
        )
    }
}

/**
 * Ce que deviendrait la place si la demande était supprimée maintenant
 * (DEV-621, M4), lu par l'appel ciblé ; `null` : confirmation comme avant
 */
function lirePhrasePlace(): Promise<string | null> {
    return lirePhrasePlaceDemande(services, getConsentRequest.value?._id)
}

/**
 * Callback pour supprimer la demande de consentement
 * @returns Promise qui se résout avec un objet contenant isFetching
 */
async function onDelete(): Promise<{ isFetching: () => boolean }> {
    const {
        fetch: fetchDeleteConsentRequest,
        isFetching: isFetchingDeleteConsentRequest,
    } = useFetchWrapper(async () => {
        if (!$deleteConsentRequest) {
            throw new Error("Service $deleteConsentRequest non disponible")
        }

        try {
            // Mapping pour convertir le type de la demande en type attendu par le service
            const typeMapping: Record<
                string,
                "sign" | "share" | "extend" | "change-owner"
            > = {
                SIGN: "sign",
                SHARE: "share",
                EXTEND: "extend",
                CHANGE_OWNER: "change-owner",
                // valeur par défaut : "sign"
            }
            const originalType = getConsentRequest.value?.type
            // On prend la valeur mappée, sinon "sign" par défaut
            const serviceType =
                typeMapping[originalType?.toUpperCase?.() ?? ""] ?? "sign"

            await $deleteConsentRequest(
                getConsentRequest.value._id,
                serviceType
            )
            // Le nombre facturé servi suit la suppression (DEV-621, M6)
            await rafraichirPlafondServi()
            emit("delete:consent", getConsentRequest.value._id)
        } catch (error) {
            console.error(
                "Erreur lors de la suppression de la demande de consentement:",
                error
            )
            throw error
        }
    })

    const promise = fetchDeleteConsentRequest()
    return promise.then(() => ({
        isFetching: () => isFetchingDeleteConsentRequest.value,
    }))
}
</script>

<template>
    <div class="meter_card_invitation flex flex-col items-center gap-6">
        <!-- ================================ -->
        <!-- CARTE D'INVITATION PARTAGE -->
        <!-- ================================ -->
        <MeterCardInvitationShare
            v-if="getConsentRequest?.type === CONSENT_REQUEST_TYPE.SHARE"
            :state="getConsentRequestData.state"
            :type="getConsentRequestData.type"
            :created-at="getConsentRequestData.createdAt"
            :url="getConsentRequestData.url"
            :request-id="getConsentRequestData.requestId"
            :is-multi-request="getConsentRequestData.isMultiRequest"
            :tracking-mails="getConsentRequestData.trackingMails"
            :user-to-email="getConsentRequestData.userToEmail"
            :source-id="getConsentRequestData.sourceId"
            :on-send-invitation="onSendInvitation"
            :on-delete="onDelete"
            :lire-phrase-place="lirePhrasePlace"
        />

        <!-- ================================ -->
        <!-- CARTE D'INVITATION SIGNATURE -->
        <!-- ================================ -->
        <MeterCardInvitationSign
            v-if="
                getConsentRequest?.type === CONSENT_REQUEST_TYPE.SIGN ||
                getConsentRequest?.type === CONSENT_REQUEST_TYPE.EXTEND ||
                getConsentRequest?.type === CONSENT_REQUEST_TYPE.CHANGE_OWNER
            "
            :state="getConsentRequestData.state"
            :type="getConsentRequestData.type"
            :created-at="getConsentRequestData.createdAt"
            :signed-at="getConsentRequestData.signedAt"
            :url="getConsentRequestData.url"
            :request-id="getConsentRequestData.requestId"
            :is-multi-request="getConsentRequestData.isMultiRequest"
            :tracking-mails="getConsentRequestData.trackingMails"
            :tracking-states="getConsentRequestData.trackingStates"
            :user-to-email="getConsentRequestData.userToEmail"
            :source-idtype="getConsentRequestData.sourceIdtype"
            :source-id="getConsentRequestData.sourceId"
            :dda-list="getConsentRequestData.ddaList"
            :on-send-invitation="onSendInvitation"
            :on-delete="onDelete"
            :lire-phrase-place="lirePhrasePlace"
        />

        <ConsentRequestSendDialog
            v-model:visible="showConsentSentModal"
            :payload="consentSentModalPayload"
            @sent="onConsentInvitationSent"
            @hide="onConsentSentModalDone"
        />
    </div>
</template>

<style scoped></style>
